import '#library/temporal.polyfill.js';
import { enumify, Enum } from '#library/enumerate.library.js';
import { asArray } from '#library/coercion.library.js';
import { getDateTimeFormat, getHemisphere, canonicalLocales, resolveLocale, isEnglish } from '#library/international.library.js';
import { normalizeUtcOffset } from '#library/temporal.library.js';
import { markConfig, markExtensible } from '#library/symbol.library.js';
import { deepMerge } from '#library/object.library.js';
import { asType } from '#library/type.library.js';
import { isString, isObject, isPlainObject, isUndefined, isDefined, isRegExp, isEmpty, isFunction } from '#library/assertion.library.js';
import { ScopedSet } from '#library/scopedset.class.js';
import { ownEntries, hasOwn, hasNoneOf } from '#library/primitive.library.js';
import { parseLogLevel } from '#library/logger.class.js';
import { evaluate } from '#library/evaluation.library.js';
import { coerceGeo } from '#library/mapper.library.js';
import { BoundedCache } from '#library/cache.class.js';
import { WeakCache } from '#library/weakcache.class.js';
import { getRuntime } from './support.runtime.js';
import { setProperty, setProperties, create, collect, normalizeLayoutOrder, resolveMonthDay, logError, generateLocalizedSnippets } from './support.util.js';
import { sym, Token } from './support.symbol.js';
import { Match, Snippet, Layout, Event, Period, Ignore, Default } from './support.default.js';
import { STATE } from './support.enum.js';
import enums from './support.enum.js';
import { registryUpdate } from './support.register.js';
/**
 * Resolves cache configuration into a bounded cache.
 *
 * @param optionsCache - An existing bounded cache, entry collection, or cache configuration.
 * @param existingCache - A cache to reuse or update when applicable.
 * @returns The resolved bounded cache, using defaults of 1000 entries and 24 hours when created from configuration.
 * @internal
 */
function resolveCache(optionsCache, existingCache) {
    const isBoundedCacheOpt = Boolean(optionsCache?.isBoundedCache || optionsCache instanceof BoundedCache);
    if (isBoundedCacheOpt) {
        return optionsCache;
    }
    else if (optionsCache instanceof Map || (optionsCache && isFunction(optionsCache.entries))) {
        const targetCache = existingCache ?? new BoundedCache();
        for (const [k, v] of optionsCache.entries())
            targetCache.setStatic(String(k).trim().toLowerCase(), String(v));
        return targetCache;
    }
    else {
        if (existingCache && (existingCache.isBoundedCache || existingCache instanceof BoundedCache)) {
            if (isDefined(optionsCache?.maxSize))
                existingCache.maxSize = optionsCache.maxSize;
            if (isDefined(optionsCache?.ttl))
                existingCache.ttl = optionsCache.ttl;
            return existingCache;
        }
        else {
            const maxSize = optionsCache?.maxSize ?? 1000;
            const ttl = optionsCache?.ttl ?? (24 * 60 * 60 * 1000);
            return new BoundedCache(maxSize, ttl);
        }
    }
}
/**
 * @internal
 * Initializes and returns Tempo state for global, local, or inherited configurations.
 *
 * @param options - Configuration options used to initialize the state
 * @param isGlobal - Whether the state serves as the global Tempo state
 * @param baseState - Optional state from which to inherit configuration and parsing settings
 * @param prevCache - Optional cache to reuse when no cache is available on the base or global state
 * @returns The initialized Tempo state
 */
export function init(options = {}, isGlobal = true, baseState, prevCache) {
    const runtime = getRuntime();
    // Global init is intentionally idempotent after first hydration; late-loaded modules must use Tempo.use().
    if (isGlobal && runtime.state && !baseState) {
        runtime.state.cache = resolveCache(options.cache, runtime.state.cache);
        return runtime.state;
    }
    const { timeZone, calendar } = getDateTimeFormat();
    const pluginsDb = baseState ? {
        terms: [...baseState.pluginsDb.terms],
        plugins: [...baseState.pluginsDb.plugins]
    } : { terms: [], plugins: [] };
    markExtensible(pluginsDb);
    markExtensible(pluginsDb.terms);
    markExtensible(pluginsDb.plugins);
    const state = (baseState ? Object.create(baseState) : {
        config: {},
        parse: {},
        pluginsDb,
        userProvidedKeys: new Set()
    });
    if (baseState) {
        state.config = Object.create(baseState.config);
        if (baseState.config.registry)
            state.config.registry = Object.create(baseState.config.registry);
        state.parse = Object.create(baseState.parse);
        state.userProvidedKeys = new Set(baseState.userProvidedKeys);
        state.installed = new ScopedSet(runtime.installed); // sandbox: delegates has() to global, isolates add()
        state.pluginsDb = pluginsDb;
    }
    state.options = options;
    const targetCache = baseState?.cache ?? runtime.state?.cache ?? prevCache;
    state.cache = resolveCache(options.cache, targetCache);
    state.weakCache = baseState ? new WeakCache() : (runtime.state?.weakCache ?? new WeakCache());
    // 1. Establish the base parsing state
    const parseState = {
        token: Token,
        result: [],
        snippet: Object.assign({}, baseState?.parse.snippet ?? Snippet),
        layout: Object.assign({}, baseState?.parse.layout ?? Layout),
        event: Object.assign({}, baseState?.parse.event ?? Event),
        period: Object.assign({}, baseState?.parse.period ?? Period),
        ignore: baseState ? { ...baseState.parse.ignore } : Object.fromEntries(asArray(Ignore).map(w => [w, w])),
        monthDay: baseState ? {
            ...baseState.parse.monthDay,
            locales: [...asArray(baseState.parse.monthDay.locales)],
            layouts: [...asArray(baseState.parse.monthDay.layouts)],
            timezones: { ...baseState.parse.monthDay.timezones },
            ...(baseState.parse.monthDay.resolvedLocales ? {
                resolvedLocales: baseState.parse.monthDay.resolvedLocales.map((l) => ({ ...l, timeZones: [...l.timeZones] }))
            } : {})
        } : resolveMonthDay({}, Default.monthDay),
        planner: baseState ? {
            ...(baseState.parse.planner.layoutOrder ? { layoutOrder: [...asArray(baseState.parse.planner.layoutOrder)] } : {}),
            ...(isDefined(baseState.parse.planner.preFilter) ? { preFilter: Boolean(baseState.parse.planner.preFilter) } : {}),
        } : {
            layoutOrder: [...asArray(Default.planner?.layoutOrder ?? Default.layoutOrder)],
            preFilter: Boolean(Default.planner?.preFilter ?? Default.preFilter),
        },
        pivot: (baseState?.parse.pivot ?? Default.pivot),
        mode: (baseState?.parse.mode ?? Default.mode),
        lazy: false,
        pattern: new Map(baseState?.parse.pattern),
        ...(baseState ? {
            ...(isDefined(baseState.parse.isAnchored) ? { isAnchored: baseState.parse.isAnchored } : {}),
            ...(isDefined(baseState.parse.anchor) ? { anchor: baseState.parse.anchor } : {}),
            ...(isDefined(baseState.parse.format) ? { format: baseState.parse.format } : {}),
            ...(isDefined(baseState.parse.term) ? { term: baseState.parse.term } : {}),
            ...(isDefined(baseState.parse.guard) ? { guard: baseState.parse.guard } : {}),
        } : {})
    };
    state.parse = markConfig(parseState);
    // 2. Establish the base configuration options
    const configDefaults = Object.fromEntries(Object.entries(Default).filter(([key]) => enums.CONFIG.has(key)));
    if (isGlobal) {
        markConfig(Object.assign(state.config, configDefaults));
        // Unfreeze the registry sub-object so that extendState can write into it at runtime.
        // Default.registry is frozen (via secure()), so we need a mutable shallow clone.
        if (state.config.registry) {
            state.config.registry = {
                ...state.config.registry,
                modifiers: { ...(state.config.registry.modifiers ?? {}) }
            };
        }
        const { timeZone, calendar } = getDateTimeFormat();
        setProperties(state.config, {
            calendar,
            timeZone,
            locale: getDateTimeFormat().locale ?? 'en-US',
            discovery: Symbol.keyFor(sym.$Tempo),
            formats: enumify(STATE.FORMAT, false),
            sphere: getHemisphere(timeZone),
            scope: 'global',
            catch: options.catch ?? false,
            silent: options.silent ?? false,
            intl: {},
        });
        Object.defineProperty(state.config, 'get', { value: function (key) { return this[key]; }, enumerable: false, writable: true, configurable: true });
    }
    else if (baseState) {
        state.config = markConfig(Object.create(baseState.config));
        if (baseState.config.registry)
            state.config.registry = Object.create(baseState.config.registry);
        state.parse = Object.create(baseState.parse);
        setProperties(state.config, {
            scope: 'local',
            catch: options.catch ?? baseState.config.catch ?? false,
            silent: options.silent ?? baseState.config.silent ?? false,
            intl: Object.create(baseState.config.intl || {}),
        });
        Object.defineProperty(state.config, 'get', { value: function (key) { return this[key]; }, enumerable: false, writable: true, configurable: true });
    }
    else {
        markConfig(Object.assign(state.config, configDefaults));
        // Unfreeze the registry sub-object so that extendState can write into it at runtime.
        if (state.config.registry) {
            state.config.registry = {
                ...state.config.registry,
                modifiers: { ...(state.config.registry.modifiers ?? {}) }
            };
        }
        setProperties(state.config, {
            calendar,
            timeZone,
            locale: getDateTimeFormat().locale ?? 'en-US',
            discovery: Symbol.keyFor(sym.$Tempo),
            formats: enumify(STATE.FORMAT, false),
            sphere: getHemisphere(timeZone),
            scope: 'local',
            silent: options.silent ?? false,
            intl: {},
        });
        Object.defineProperty(state.config, 'get', { value: function (key) { return this[key]; }, enumerable: false, writable: true, configurable: true });
        if (isDefined(options.catch))
            setProperty(state.config, 'catch', options.catch);
        if (isDefined(options.silent))
            setProperty(state.config, 'silent', options.silent);
    }
    // 3. Initialize registries that need objects
    state.OPTION = new Set(Object.keys(configDefaults));
    state.ZONED_DATE_TIME = new Set(['year', 'month', 'day', 'hour', 'minute', 'second', 'millisecond', 'microsecond', 'nanosecond', 'offset', 'timeZone', 'calendar']);
    if (isGlobal)
        runtime.state = state;
    return state;
}
/** @internal Extend a Tempo state with new options (Shadowing) */
const CANONICAL_OPTION_KEYS = Object.assign(Object.create(null), {
    timezone: 'timeZone',
    timestamp: 'timeStamp',
    monthday: 'monthDay',
    errorhandling: 'error',
});
/**
 * Applies configuration overrides to an existing state.
 *
 * @param state - The state to update
 * @param options - Configuration options to apply
 * @returns `true` if the changes require parsing patterns to be regenerated, `false` otherwise.
 */
export function extendState(state, options) {
    let patternsDirty = false;
    const clearLocalization = () => {
        if (state.parse.monthMap || state.parse.weekdayMap) {
            delete state.parse.monthMap;
            delete state.parse.weekdayMap;
            state.parse.snippet[Token.mm] = Snippet[Token.mm];
            state.parse.snippet[Token.wkd] = Snippet[Token.wkd];
            if (state.parse.localizedEvents) {
                state.parse.localizedEvents
                    .forEach((k) => { delete state.parse.event[k]; });
                delete state.parse.localizedEvents;
            }
            patternsDirty = true;
        }
    };
    ownEntries(options).forEach(([rawKey, optVal]) => {
        if (isUndefined(optVal))
            return;
        const optKey = isString(rawKey)
            ? (CANONICAL_OPTION_KEYS[rawKey.toLowerCase()] ?? rawKey)
            : rawKey;
        state.userProvidedKeys.add(optKey);
        state.userProvidedKeys.add(rawKey);
        const preserveSupplier = isFunction(optVal) && state.config?.scope !== 'local';
        const evaluatedVal = (['timeZone', 'calendar', 'locale', 'sphere', 'pivot'].includes(optKey) && !preserveSupplier)
            ? evaluate(optVal)
            : optVal;
        const arg = asType(evaluatedVal);
        switch (optKey) {
            case 'error': {
                const policy = String(arg.value).toLowerCase();
                switch (policy) {
                    case 'throw':
                        if (isUndefined(options.catch))
                            setProperty(state.config, 'catch', false);
                        if (isUndefined(options.silent))
                            setProperty(state.config, 'silent', false);
                        break;
                    case 'catch':
                        if (isUndefined(options.catch))
                            setProperty(state.config, 'catch', true);
                        if (isUndefined(options.silent))
                            setProperty(state.config, 'silent', false);
                        break;
                    case 'silent':
                        if (isUndefined(options.catch))
                            setProperty(state.config, 'catch', true);
                        if (isUndefined(options.silent))
                            setProperty(state.config, 'silent', true);
                        break;
                    case 'log':
                        if (isUndefined(options.catch))
                            setProperty(state.config, 'catch', true);
                        if (isUndefined(options.silent))
                            setProperty(state.config, 'silent', false);
                        if (isUndefined(options.debug))
                            setProperty(state.config, 'debug', parseLogLevel('warn'));
                        break;
                    default:
                        logError(`[Tempo#extend] Invalid error option policy: ${String(arg.value)}. Expected 'throw', 'catch', 'silent', or 'log'.`, state.config);
                        break;
                }
                break;
            }
            case 'monthDay':
                state.parse.monthDay = resolveMonthDay(arg.value, state.parse.monthDay);
                break;
            case 'timeZone': {
                const zone = String(arg.value).toLowerCase();
                const resolvedZone = options.timeZones?.[zone] ?? state.config.timeZones?.[zone] ?? enums.TIMEZONE[zone] ?? normalizeUtcOffset(String(arg.value));
                setProperty(state.config, 'timeZone', preserveSupplier ? optVal : resolvedZone);
                const hasSuppliedSphere = isDefined(options.sphere) || (state.userProvidedKeys?.has('sphere') && isDefined(state.config.sphere));
                if (!hasSuppliedSphere)
                    setProperty(state.config, 'sphere', getHemisphere(resolvedZone));
                break;
            }
            case 'calendar':
                setProperty(state.config, 'calendar', preserveSupplier ? optVal : String(arg.value));
                break;
            case 'locale': {
                if (preserveSupplier) {
                    setProperty(state.config, 'locale', optVal);
                    break;
                }
                const finalLocale = resolveLocale(arg.value);
                if (finalLocale) {
                    setProperty(state.config, 'locale', finalLocale);
                    if (isEnglish(finalLocale))
                        clearLocalization();
                }
                break;
            }
            case 'localeInfo':
                setProperty(state.config, 'localeInfo', Boolean(arg.value));
                break;
            case 'discovery':
                setProperty(state.config, 'discovery', arg.value);
                break;
            case 'registry':
                if (isObject(arg.value)) {
                    state.config.registry = {
                        ...state.config.registry,
                        modifiers: { ...(state.config.registry?.modifiers ?? {}) }
                    };
                    if (arg.value.formats) {
                        const extended = state.config.registry.formats ? Enum.extend(state.config.registry.formats, arg.value.formats) : arg.value.formats;
                        setProperty(state.config.registry, 'formats', extended);
                    }
                    if (arg.value.locales) {
                        const extended = state.config.registry.locales ? Enum.extend(state.config.registry.locales, arg.value.locales) : arg.value.locales;
                        setProperty(state.config.registry, 'locales', extended);
                    }
                    if (arg.value.modifiers) {
                        // Deep-merge user modifiers with existing defaults so English keywords remain active
                        const existing = state.config.registry.modifiers ?? {};
                        const merged = {};
                        // normalize existing defaults into arrays
                        for (const [op, words] of Object.entries(existing))
                            merged[op] = [...asArray(words)];
                        // merge incoming user modifiers
                        for (const [op, words] of Object.entries(arg.value.modifiers)) {
                            const current = merged[op] ?? [];
                            merged[op] = [...new Set([...current, ...asArray(words)])];
                        }
                        setProperty(state.config.registry, 'modifiers', merged);
                        patternsDirty = true;
                    }
                    if (arg.value.tokens) {
                        const existing = state.config.registry.tokens ?? {};
                        setProperty(state.config.registry, 'tokens', { ...existing, ...arg.value.tokens });
                    }
                    if (arg.value.dialects) {
                        const existing = state.config.registry.dialects ?? {};
                        setProperty(state.config.registry, 'dialects', { ...existing, ...arg.value.dialects });
                    }
                    if (arg.value.numbers) {
                        const existing = state.config.registry.numbers ?? {};
                        setProperty(state.config.registry, 'numbers', { ...existing, ...arg.value.numbers });
                        if (state.config?.scope === 'global')
                            registryUpdate('NUMBER', arg.value.numbers);
                        patternsDirty = true;
                    }
                    if (arg.value.ordinals) {
                        const existing = state.config.registry.ordinals ?? {};
                        setProperty(state.config.registry, 'ordinals', { ...existing, ...arg.value.ordinals });
                        if (state.config?.scope === 'global')
                            registryUpdate('ORDINAL', arg.value.ordinals);
                        patternsDirty = true;
                    }
                    const parseMap = {
                        snippets: 'snippet',
                        layouts: 'layout',
                        events: 'event',
                        periods: 'period',
                        ignores: 'ignore'
                    };
                    ownEntries(arg.value).forEach(([k, v]) => {
                        if (isString(k) && k in parseMap) {
                            const targetKey = parseMap[k];
                            state.userProvidedKeys.add(targetKey);
                            patternsDirty = true;
                            if (!hasOwn(state.parse, targetKey))
                                state.parse[targetKey] = create(state.parse, targetKey);
                            const rule = state.parse[targetKey];
                            if (['snippet', 'layout'].includes(targetKey)) {
                                collect(rule, v, (val) => {
                                    if (targetKey === 'snippet') {
                                        // ReDoS Mitigation: Snippet patterns should be kept as simple as possible.
                                        // While length limits and backtrack detection offer baseline protection, 
                                        // residual risk remains from crafted patterns that evade these checks.
                                        // If snippets are ever sourced from untrusted configuration inputs, consider
                                        // implementing a timeout wrapper around the RegExp constructor call for defense-in-depth.
                                        const pattern = isRegExp(val) ? val.source : String(val);
                                        if (pattern.length > 500) {
                                            logError(`[Tempo#extend] Snippet pattern too long (max 500 chars).`, state.config);
                                            return new RegExp(Match.escape(pattern));
                                        }
                                        if (Match.backtrack.test(pattern)) {
                                            logError(`[Tempo#extend] Snippet contains suspicious nested quantifiers.`, state.config);
                                            return new RegExp(Match.escape(pattern));
                                        }
                                        return new RegExp(pattern);
                                    }
                                    return isRegExp(val) ? val.source : val;
                                });
                            }
                            else {
                                asArray(v).forEach(elm => {
                                    if (isObject(elm))
                                        Object.assign(rule, elm);
                                    else if (isString(elm))
                                        Object.assign(rule, { [elm]: elm });
                                });
                            }
                        }
                    });
                }
                break;
            case 'sphere':
                if (preserveSupplier) {
                    setProperty(state.config, 'sphere', optVal);
                }
                else if (isDefined(arg.value)) {
                    setProperty(state.config, 'sphere', arg.value);
                }
                break;
            case 'geo':
            case 'latitude':
            case 'lat':
            case 'longitude':
            case 'lng':
            case 'lon':
            case 'long': {
                const geoConfig = coerceGeo(options);
                if (geoConfig)
                    setProperty(state.config, 'geo', geoConfig);
                break;
            }
            case 'catch':
                setProperty(state.config, 'catch', Boolean(arg.value));
                break;
            case 'silent':
                setProperty(state.config, 'silent', Boolean(arg.value));
                break;
            case 'pivot': {
                const pivot = parseInt(String(arg.value));
                state.parse.pivot = (Number.isFinite(pivot) && pivot >= 0 && pivot <= 99) ? pivot : Default.pivot;
                break;
            }
            case 'mode':
                state.parse.mode = arg.value;
                break;
            case 'intl':
                state.config.intl = deepMerge(state.config.intl ?? {}, arg.value);
                break;
            case 'planner':
                if (isDefined(arg.value.layoutOrder))
                    state.parse.planner.layoutOrder = normalizeLayoutOrder(arg.value.layoutOrder);
                if (isDefined(arg.value.preFilter))
                    state.parse.planner.preFilter = Boolean(arg.value.preFilter);
                break;
            case 'layoutOrder':
                state.parse.planner.layoutOrder = normalizeLayoutOrder(arg.value);
                break;
            case 'preFilter':
                state.parse.planner.preFilter = Boolean(arg.value);
                break;
            case 'timeStamp': {
                const unit = (isString(arg.value) ? arg.value : arg.value?.unit)?.trim()?.toLowerCase();
                if (isUndefined(unit) || !['ss', 'ms', 'us', 'ns'].includes(unit)) {
                    logError(`[Tempo#extend] Invalid timeStamp unit: ${String(unit ?? arg.value)}. Expected 'ss', 'ms', 'us', or 'ns'.`, state.config);
                    break;
                }
                setProperty(state.config, optKey, unit);
                break;
            }
            case 'debug':
                setProperty(state.config, 'debug', parseLogLevel(arg.value));
                break;
            case 'cache':
                if (isObject(arg.value)) {
                    if (arg.value.maxSize !== undefined)
                        state.cache.maxSize = arg.value.maxSize;
                    if (arg.value.ttl !== undefined)
                        state.cache.ttl = arg.value.ttl;
                }
                else {
                    setProperty(state.config, 'cache', arg.value);
                }
                break;
            case 'extends':
                // Inherited configuration files/URLs are resolved prior to or during config resolution
                break;
            case 'format':
                setProperty(state.config, 'format', arg.value);
                break;
            case 'dialect':
                if (isString(arg.value) || isFunction(arg.value)) {
                    setProperty(state.config, 'dialect', arg.value);
                }
                else {
                    logError(`[Tempo#extend] Invalid dialect option: ${String(arg.value)}. Expected a string or function.`, state.config);
                }
                break;
            case 'pluginOptions':
                if (isObject(arg.value)) {
                    const existing = state.config.pluginOptions ?? {};
                    setProperty(state.config, 'pluginOptions', { ...existing, ...arg.value });
                    // Keep legacy plugins dictionary in sync for backward compatibility
                    const existingLegacy = state.config.plugins ?? {};
                    setProperty(state.config, 'plugins', { ...existingLegacy, ...arg.value });
                }
                break;
            case 'plugins':
                /** @deprecated Passing configuration dictionaries in 'plugins' is deprecated. Use 'pluginOptions' instead. */
                if (isPlainObject(arg.value) && hasNoneOf(arg.value, 'install', 'key')) {
                    const existing = state.config.plugins ?? {};
                    setProperty(state.config, 'plugins', { ...existing, ...arg.value });
                    const existingOpts = state.config.pluginOptions ?? {};
                    setProperty(state.config, 'pluginOptions', { ...existingOpts, ...arg.value });
                }
                break;
            default:
                setProperty(state.config, optKey, arg.value);
                break;
        }
    });
    const locale = (isFunction(state.config.locale) && state.config?.scope !== 'local') ? undefined : evaluate(state.config.locale);
    if (locale) {
        const locales = canonicalLocales(locale);
        if (locales.length > 0 && !isEnglish(locales)) {
            const { snippets, monthMap, weekdayMap, events } = generateLocalizedSnippets(locales);
            state.parse.monthMap = monthMap;
            state.parse.weekdayMap = weekdayMap;
            Object.assign(state.parse.snippet, snippets);
            // Map to exact lexer Tokens to override default layout placeholders
            const defaultMm = Snippet[Token.mm].source.replace(/^\(\?<mm>|\)$/g, '');
            const defaultWkd = Snippet[Token.wkd].source.replace(/^\(\?<wkd>|\)$/g, '');
            state.parse.snippet[Token.mm] = new RegExp(`(?<mm>${defaultMm}|${snippets.mmm})`, 'i');
            state.parse.snippet[Token.wkd] = new RegExp(`(?<wkd>${defaultWkd}|${snippets.www})`, 'i');
            if (!isEmpty(events)) {
                Object.assign(state.parse.event, events);
                state.parse.localizedEvents = Object.keys(events);
                // Register new localized aliases with the AliasEngine
                if (state.aliasEngine) {
                    // Ensure we don't corrupt global state if we are a local instance
                    if (state.config.scope === 'local' && state.aliasEngine.depth === 0) {
                        if (isFunction(state.aliasEngine.fork))
                            state.aliasEngine = state.aliasEngine.fork(state.config);
                    }
                    state.aliasEngine.registerAliases('evt', ownEntries(events));
                }
            }
            patternsDirty = true;
        }
        else if (locales.length > 0) {
            clearLocalization();
        }
    }
    return patternsDirty;
}
